import { TranslateDirective, TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from "@angular/material/dialog";

import { CustomerService } from "../../../apis/networkdashboard/rest/services/customer.service";
import { AuthService } from "../../../services/authservice";
import { SelfserviceToggle } from "../../selfservice/selfservice-toggle/selfservice-toggle";

interface DialogRolesData {
  title: string;
  description: string;
  roles: string[];
  need?: string[];
}

@Component({
  selector: "dialog-roles",
  templateUrl: "dialog-roles.html",
  styleUrl: "dialog-roles.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, SelfserviceToggle, TranslateDirective, TranslatePipe, MatDialogClose],
})
export class DialogRoles {
  protected data = inject<DialogRolesData>(MAT_DIALOG_DATA);

  private dialogRef = inject(MatDialogRef<DialogRoles>);
  protected auth = inject(AuthService);
  private customers = inject(CustomerService);
  private translate = inject(TranslateService);

  private roles = [
    {
      key: "Infrabeheerder",
      title: this.translate.instant("Role.Infrabeheerder.Title"),
      description: this.translate.instant("Role.Infrabeheerder.Description"),
    },
    {
      key: "Infraverantwoordelijke",
      title: this.translate.instant("Role.Infraverantwoordelijke.Title"),
      description: this.translate.instant("Role.Infraverantwoordelijke.Description"),
    },
    {
      key: "Beveiligingsverantwoordelijke",
      title: this.translate.instant("Role.Beveiligingsverantwoordelijke.Title"),
      description: this.translate.instant("Role.Beveiligingsverantwoordelijke.Description"),
    },
    {
      key: "SURFwireless-beheerder",
      title: this.translate.instant("Role.SURFwireless-beheerder.Title"),
      description: this.translate.instant("Role.SURFwireless-beheerder.Description"),
    },
    {
      key: "SURFwireless-verantwoordelijke",
      title: this.translate.instant("Role.SURFwireless-verantwoordelijke.Title"),
      description: this.translate.instant("Role.SURFwireless-verantwoordelijke.Description"),
    },
    {
      key: "Domeinnamenverantwoordelijke",
      title: this.translate.instant("Role.Domeinnamenverantwoordelijke.Title"),
      description: this.translate.instant("Role.Domeinnamenverantwoordelijke.Description"),
    },
    {
      key: "DNS-Beheerder",
      title: this.translate.instant("Role.DNSbeheerder.Title"),
      description: this.translate.instant("Role.DNSbeheerder.Description"),
    },
  ];

  private roleFilter = this.data.need ?? [];

  protected currentStep = signal(1);
  protected requestedRoles = signal([...this.data.roles]);

  private contacts = resource({
    params: () => this.auth.viewingCustomer().customerId || undefined,
    loader: ({ params: customerId }) => this.customers.getCustomerContactPersons({ customer_id: customerId }),
  });

  protected AccessError = computed(() => (this.contacts.error() as { status?: number } | undefined)?.status === 403);
  protected noContactsFound = computed(() => !!this.contacts.error() && !this.AccessError());
  protected isFirstStep = computed(() => this.currentStep() === 1);
  protected isSecondStep = computed(() => this.currentStep() === 2);

  protected gotoSecondStep = () => this.currentStep.set(2);

  private requestedRolesString = computed(() =>
    this.requestedRoles()
      .map((role) => (!this.auth.hasRole([role]) ? `*${role}` : role))
      .sort((a, _b) => (a.startsWith("*") ? -1 : 1))
      .join("%0D"),
  );

  protected generatedMail = computed(() => {
    const contacts = this.contacts.hasValue() ? this.contacts.value() : [];
    const emails = contacts.map((c) => c.email);
    const names = contacts.map((c) => `${c.first_name} ${c.last_name}`);
    return (
      `mailto:${emails.join(",")}`
      + `?&subject=Verzoek voor wijziging SAB rollen voor SURF Netwerk Dashboard`
      + `&body=Beste ${names.join(", ")}, `
      + `${this.translate.instant("Roles.Request.ContentEmail1")} `
      + `${this.auth.currentUser().displayName}, ${this.auth.currentUser().email} `
      + `${this.translate.instant("Roles.Request.ContentEmail2")} `
      + `${this.requestedRolesString()} `
      + `${this.translate.instant("Roles.Request.ContentEmail3")} `
      + `${this.auth.currentUser().displayName}, ${this.auth.currentUser().email} `
      + `${this.translate.instant("Roles.Request.ContentEmail4")} `
      + `${this.requestedRolesString()} `
      + `${this.translate.instant("Roles.Request.ContentEmail5")}`
    );
  });

  protected filteredRoles = computed(() =>
    this.roleFilter.length > 0 ?
      this.roles.filter((role) => this.roleFilter.includes(role.key) || this.data.roles.includes(role.key))
    : this.roles,
  );

  protected hasRole(role: string) {
    return this.data.roles.includes(role);
  }

  protected isRequested(role: string) {
    return this.requestedRoles().includes(role);
  }

  protected toggleRole(role: string) {
    this.requestedRoles.update((roles) => (roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role]));
  }

  protected closeDialog() {
    this.dialogRef.close();
  }
}
